import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Star, ThumbsUp, ThumbsDown, Filter, ChevronDown, CheckCircle, AlertCircle } from 'lucide-react';
import { ratingService } from '../../services/ratingService';
import { productService } from '../../services/productService'; // Keep for product details if needed
import { sellerService } from '../../services/sellerService'; // NEW: Import seller service
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabase';
import ReviewModal from '../product-reviews-ratings/components/ReviewModal'; // Re-use the existing modal
import Footer from '../homepage/components/Footer';

const SellerReviewsRatings = () => { // Renamed component
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading: authLoading, initialCheckComplete } = useAuth();
  
  const sellerIdentifier = searchParams?.get('seller') || searchParams?.get('sellerId'); // NEW: Get seller identifier
  
  const [seller, setSeller] = useState(null); // NEW: State for seller data
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterRating, setFilterRating] = useState('all');
  const [sortBy, setSortBy] = useState('recent');
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);

  const [directUser, setDirectUser] = useState(null);
  const [sessionChecked, setSessionChecked] = useState(false);
  const [userVotes, setUserVotes] = useState({});

  useEffect(() => {
    if (user) {
      setDirectUser(null);
      setSessionChecked(true);
      return;
    }

    if (!sessionChecked) {
      const checkSession = async () => {
        try {
          const { data: { session } } = await supabase?.auth?.getSession();
          if (session?.user) {
            console.log('SellerReviews - Found user via direct session check:', session?.user?.id);
            setDirectUser(session?.user);
          } else {
            console.log('SellerReviews - No session found in direct check');
            setDirectUser(null);
          }
        } catch (err) {
          console.error('SellerReviews - Direct session check failed:', err);
          setDirectUser(null);
        } finally {
          setSessionChecked(true);
        }
      };

      checkSession();
    }
  }, [user, sessionChecked]);

  const effectiveUser = user || directUser;

  const isAuthReady = (initialCheckComplete || sessionChecked) && !authLoading;

  useEffect(() => {
    console.log('SellerReviews - Auth state:', {
      hasUser: !!user,
      hasDirectUser: !!directUser,
      effectiveUser: !!effectiveUser,
      userId: effectiveUser?.id,
      userEmail: effectiveUser?.email,
      authLoading,
      initialCheckComplete,
      sessionChecked,
      timestamp: new Date()?.toISOString()
    });
  }, [user, directUser, effectiveUser, authLoading, initialCheckComplete, sessionChecked]);

  useEffect(() => {
    if (sellerIdentifier && (initialCheckComplete || sessionChecked)) {
      loadSellerAndReviews();
    }
  }, [sellerIdentifier, filterRating, sortBy, initialCheckComplete, sessionChecked]);

  const loadSellerAndReviews = async () => {
    try {
      setLoading(true);
      setError(null);

      // NEW: Fetch seller data
      const [sellerData, reviewsData] = await Promise.all([
        sellerService?.getPublicStorefrontData(sellerIdentifier), // Use the correct service function
        ratingService?.getSellerRatings(sellerIdentifier) // NEW: Fetch seller ratings
      ]);

      if (!sellerData) {
        throw new Error('Seller not found.');
      }
      setSeller(sellerData);
      
      let filteredReviews = reviewsData;
      
      if (filterRating !== 'all') {
        const targetRating = parseInt(filterRating);
        filteredReviews = reviewsData?.filter(r => r?.rating === targetRating);
      }

      if (sortBy === 'recent') {
        filteredReviews?.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      } else if (sortBy === 'helpful') {
        filteredReviews?.sort((a, b) => (b?.helpfulCount || 0) - (a?.helpfulCount || 0));
      } else if (sortBy === 'rating-high') {
        filteredReviews?.sort((a, b) => b?.rating - a?.rating);
      } else if (sortBy === 'rating-low') {
        filteredReviews?.sort((a, b) => a?.rating - b?.rating);
      }

      setReviews(filteredReviews);

      if (effectiveUser) {
        const votes = {};
        for (const review of filteredReviews) {
          const userVote = await ratingService?.getUserVote(review?.id, 'seller'); // NEW: 'seller' type
          if (userVote) {
            votes[review?.id] = userVote;
          }
        }
        setUserVotes(votes);
      }
    } catch (err) {
      console.error('Error loading seller and reviews:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const getRatingDistribution = () => {
    const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    reviews?.forEach(review => {
      if (distribution?.[review?.rating] !== undefined) {
        distribution[review.rating]++;
      }
    });
    return distribution;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const handleWriteReview = () => {
    console.log('handleWriteReview - Auth check:', { 
      hasUser: !!user,
      hasDirectUser: !!directUser,
      effectiveUser: !!effectiveUser,
      userId: effectiveUser?.id,
      authLoading,
      initialCheckComplete,
      sessionChecked,
      isAuthReady,
      timestamp: new Date()?.toISOString()
    });
    
    if (!effectiveUser) {
      console.log('handleWriteReview - No authenticated user, redirecting to signin');
      sessionStorage.setItem('returnTo', `/seller-reviews-ratings?sellerId=${sellerIdentifier}`); // NEW: Return to seller reviews
      navigate('/signin');
      return;
    }
    
    console.log('handleWriteReview - User authenticated, opening review modal');
    setIsReviewModalOpen(true);
  };

  const handleReviewSubmitted = () => {
    loadSellerAndReviews();
  };

  const handleVote = async (reviewId, voteType) => {
    console.log('handleVote - Starting vote:', { reviewId, voteType, hasUser: !!effectiveUser });
    
    if (!effectiveUser) {
      console.log('handleVote - No authenticated user, redirecting to signin');
      sessionStorage.setItem('returnTo', `/seller-reviews-ratings?sellerId=${sellerIdentifier}`); // NEW: Return to seller reviews
      navigate('/signin');
      return;
    }

    try {
      const result = await ratingService?.voteOnReview(reviewId, 'seller', voteType); // NEW: 'seller' type
      console.log('handleVote - Vote result:', result);

      setUserVotes(prev => {
        const newVotes = { ...prev };
        if (result?.removed) {
          delete newVotes?.[reviewId];
        } else {
          newVotes[reviewId] = voteType;
        }
        return newVotes;
      });

      await loadSellerAndReviews();
    } catch (err) {
      console.error('Error voting on review:', err);
      setError('Failed to record your vote. Please try again.');
    }
  };

  if (loading && !isAuthReady && !effectiveUser) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Verifying authentication...</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-800">Error Loading Reviews</h3>
              <p className="text-red-600 text-sm mt-1">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const distribution = getRatingDistribution();
  const totalReviews = reviews?.length;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="container mx-auto px-4 py-8 pt-20">
        {/* Seller Header */}
        {seller && ( // NEW: Use seller data
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div className="flex items-start gap-6">
              <img
                src={seller?.storefrontBannerUrl || seller?.logoUrl || '/api/placeholder/120/120'}
                alt={seller?.businessName}
                className="w-24 h-24 object-cover rounded-lg"
              />
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-gray-900 mb-2">{seller?.businessName}</h1>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center">
                      {[...Array(5)]?.map((_, i) => (
                        <Star
                          key={i}
                          className={`w-5 h-5 ${
                            i < Math.floor(seller?.rating || (reviews?.length > 0 ? reviews.reduce((acc, curr) => acc + curr.rating, 0) / reviews.length : 0))
                              ? 'fill-yellow-400 text-yellow-400' :'text-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-lg font-semibold text-gray-900">
                      {(seller?.rating || (reviews?.length > 0 ? reviews.reduce((acc, curr) => acc + curr.rating, 0) / reviews.length : 0))?.toFixed(1)}
                    </span>
                  </div>
                  <span className="text-gray-600">
                    {seller?.totalRatings || reviews?.length || 0} {(seller?.totalRatings || reviews?.length) === 1 ? 'review' : 'reviews'}
                  </span>
                </div>
              </div>
              <Button
                onClick={handleWriteReview}
                disabled={authLoading || !isAuthReady}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {effectiveUser ? 'Write a Review' : 'Sign In to Review'}
              </Button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Rating Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-6 sticky top-4">
              <h3 className="font-semibold text-lg mb-4">Rating Distribution</h3>
              
              <div className="space-y-3">
                {[5, 4, 3, 2, 1]?.map((rating) => {
                  const count = distribution?.[rating] || 0;
                  const percentage = totalReviews > 0 ? (count / totalReviews) * 100 : 0;
                  
                  return (
                    <button
                      key={rating}
                      onClick={() => setFilterRating(filterRating === rating?.toString() ? 'all' : rating?.toString())}
                      className={`w-full flex items-center gap-2 text-sm hover:bg-gray-50 p-2 rounded transition-colors ${
                        filterRating === rating?.toString() ? 'bg-blue-50' : ''
                      }`}
                    >
                      <span className="font-medium w-8">{rating} ★</span>
                      <div className="flex-1 bg-gray-200 rounded-full h-2 overflow-hidden">
                        <div
                          className="bg-yellow-400 h-full transition-all duration-300"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <span className="text-gray-600 w-12 text-right">{count}</span>
                    </button>
                  );
                })}
              </div>

              {filterRating !== 'all' && (
                <Button
                  onClick={() => setFilterRating('all')}
                  className="w-full mt-4 bg-gray-100 hover:bg-gray-200 text-gray-700"
                >
                  Clear Filter
                </Button>
              )}
            </div>
          </div>

          {/* Reviews List */}
          <div className="lg:col-span-3">
            {/* Filters and Sort */}
            <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
              <div className="flex items-center justify-between">
                <h2 className="font-semibold text-lg">
                  {totalReviews} {totalReviews === 1 ? 'Review' : 'Reviews'}
                </h2>
                
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e?.target?.value)}
                      className="appearance-none bg-gray-50 border border-gray-300 rounded-lg px-4 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="recent">Most Recent</option>
                      <option value="helpful">Most Helpful</option>
                      <option value="rating-high">Highest Rated</option>
                      <option value="rating-low">Lowest Rated</option>
                    </select>
                    <ChevronDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
                  </div>
                </div>
              </div>
            </div>

            {/* Reviews */}
            <div className="space-y-4">
              {reviews?.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                  <p className="text-gray-600 mb-4">No reviews yet. Be the first to review this seller!</p> {/* NEW: Text change */}
                  <Button
                    onClick={handleWriteReview}
                    disabled={authLoading || !isAuthReady}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    Write First Review
                  </Button>
                </div>
              ) : (
                reviews?.map((review) => (
                  <ReviewCard 
                    key={review?.id} 
                    review={review}
                    userVote={userVotes?.[review?.id]}
                    onVote={handleVote}
                    isAuthenticated={!!effectiveUser}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Review Modal */}
      <ReviewModal
        isOpen={isReviewModalOpen}
        onClose={() => setIsReviewModalOpen(false)}
        seller={seller} // NEW: Pass seller prop
        user={effectiveUser}
        onReviewSubmitted={handleReviewSubmitted}
      />

      {/* Footer */}
      <Footer />
    </div>
  );
};

// Re-use ReviewCard component as is, it handles product and component reviews implicitly
const ReviewCard = ({ review, userVote, onVote, isAuthenticated }) => {
  const { user: authUser } = useAuth();
  
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const isOwnReview = authUser?.id === review?.userId;

  const handleHelpfulClick = () => {
    if (isOwnReview) {
      console.log('ReviewCard - Cannot vote on own review');
      return;
    }
    console.log('ReviewCard - Helpful clicked:', { reviewId: review?.id, isAuthenticated });
    onVote?.(review?.id, 'helpful');
  };

  const handleNotHelpfulClick = () => {
    if (isOwnReview) {
      console.log('ReviewCard - Cannot vote on own review');
      return;
    }
    console.log('ReviewCard - Not Helpful clicked:', { reviewId: review?.id, isAuthenticated });
    onVote?.(review?.id, 'not_helpful');
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold">
            {review?.user?.firstName?.[0]}{review?.user?.lastName?.[0]}
          </div>
          <div>
            <h4 className="font-semibold text-gray-900">
              {review?.user?.firstName} {review?.user?.lastName}
            </h4>
            <div className="flex items-center gap-2">
              <div className="flex items-center">
                {[...Array(5)]?.map((_, i) => (
                  <Star
                    key={i}
                    className={`w-4 h-4 ${
                      i < review?.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
                    }`}
                  />
                ))}
              </div>
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span className="text-xs text-gray-600">Verified Purchase</span>
            </div>
          </div>
        </div>
        <span className="text-sm text-gray-500">{formatDate(review?.createdAt)}</span>
      </div>
      {review?.review && (
        <p className="text-gray-700 mb-4 leading-relaxed">{review?.review}</p>
      )}
      <div className="flex items-center gap-4 pt-4 border-t border-gray-100">
        <button 
          onClick={handleHelpfulClick}
          disabled={!isAuthenticated || isOwnReview}
          className={`flex items-center gap-2 text-sm transition-colors ${
            userVote === 'helpful' ?'text-blue-600 font-semibold' :'text-gray-600 hover:text-blue-600'
          } ${!isAuthenticated || isOwnReview ? 'opacity-50 cursor-not-allowed' : ''}`}
          title={
            isOwnReview 
              ? 'You cannot vote on your own review' 
              : !isAuthenticated 
                ? 'Sign in to vote' :'Mark as helpful'
          }
        >
          <ThumbsUp className={`w-4 h-4 ${userVote === 'helpful' ? 'fill-blue-600' : ''}`} />
          <span>Helpful ({review?.helpfulCount || 0})</span>
        </button>
        <button 
          onClick={handleNotHelpfulClick}
          disabled={!isAuthenticated || isOwnReview}
          className={`flex items-center gap-2 text-sm transition-colors ${
            userVote === 'not_helpful' ?'text-red-600 font-semibold' :'text-gray-600 hover:text-red-600'
          } ${!isAuthenticated || isOwnReview ? 'opacity-50 cursor-not-allowed' : ''}`}
          title={
            isOwnReview 
              ? 'You cannot vote on your own review' 
              : !isAuthenticated 
                ? 'Sign in to vote' :'Mark as not helpful'
          }
        >
          <ThumbsDown className={`w-4 h-4 ${userVote === 'not_helpful' ? 'fill-red-600' : ''}`} />
          <span>Not Helpful ({review?.notHelpfulCount || 0})</span>
        </button>
      </div>
    </div>
  );
};

export default SellerReviewsRatings;